import { Box } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Currency, LoanStatusExternal, ProductExternal, PurchaseCountry } from '@nemuru-eng/js-api-client';
import { useEffect, useRef } from 'react';

import { ProductExternalScoreCard } from '@/components/modules/Metrics/MetricAggregateViews/components/ProductExternalScoreCard';
import { useMetricAggregateViews } from '@/components/modules/Metrics/MetricAggregateViews/hooks/useMetricAggregateViews';
import { TimeInterval } from '@/components/modules/Metrics/shared/entities/TimeInterval';
import { useTimeIntervalLabel } from '@/components/modules/Metrics/shared/hooks/useTimeIntervalLabel';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface MetricAggregateViewsByProductExternalProps {
  currency: Currency;
  timeInterval: TimeInterval;
  loanStatusExternal: LoanStatusExternal[];
  productExternal: ProductExternal[];
  purchaseCountries?: PurchaseCountry[];
}

export const MetricAggregateViewsByProductExternal = ({
  currency,
  timeInterval,
  loanStatusExternal,
  productExternal,
  purchaseCountries,
}: MetricAggregateViewsByProductExternalProps) => {
  const {
    state: { metricAggregateQueryBuilder: qb, metricsRefreshToken },
  } = useOperationsContext();

  const timeIntervalLabel = useTimeIntervalLabel(timeInterval);
  const { customBorderRadius } = useCustomTheme();

  const { getMetricAggregateViews, filteredMetrics, hasRequested } = useMetricAggregateViews({
    currency,
    timeInterval,
    productExternal,
    purchaseCountries,
    loanStatusExternal,
  });

  const lastToken = useRef(metricsRefreshToken);

  useEffect(() => {
    if (qb) {
      getMetricAggregateViews();
    }
  }, [qb, timeInterval]);

  useEffect(() => {
    if (!qb) return;
    if (lastToken.current === metricsRefreshToken) return;
    lastToken.current = metricsRefreshToken;
    void getMetricAggregateViews(true);
  }, [qb, metricsRefreshToken]);

  return (
    <Box flexGrow={1}>
      <Box mb={3}>
        <Font variant="h4" color="title">
          <strong>
            <Text tId="PRODUCT_EXTERNAL_TITLE" />
          </strong>
        </Font>
        <Font variant="body2" color="textSecondary">
          <Text tId="PRODUCT_EXTERNAL_DESCRIPTION" dynamicDataTexts={[timeIntervalLabel.toLocaleLowerCase()]} />
        </Font>
      </Box>
      <Box display={'flex'} flexDirection={'column'} gridGap={16}>
        {hasRequested === false &&
          productExternal.map((i) => (
            <Skeleton
              key={`skeleton-mav-product-${i}`}
              variant="rect"
              width={'100%'}
              height={66}
              style={{ borderRadius: customBorderRadius.paper }}
            />
          ))}
        {hasRequested === true &&
          productExternal.map((product, i) => (
            <Box key={i} display={'flex'}>
              <Box flexGrow={1} className="appear-anim">
                <ProductExternalScoreCard
                  currency={currency}
                  timeInterval={timeInterval}
                  productExternal={product}
                  filteredMetrics={filteredMetrics}
                />
              </Box>
            </Box>
          ))}
      </Box>
    </Box>
  );
};
